import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[7px] text-[13px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default:
					'bg-gradient-to-br from-accent to-accent-purple text-black font-bold hover:opacity-85 hover:-translate-y-px',
				accent: 'bg-accent text-black font-semibold hover:bg-accent/90',
				// I valori sono quelli di `.btn-cyan` nel mock v11: fondo al 15%, bordo
				// al 35%. A 8/40 il bottone si leggeva più come un contorno che come un
				// pieno tenue, e accanto alle card staccava meno del previsto.
				accentSoft:
					'border border-accent/35 bg-accent/15 text-accent font-semibold hover:bg-accent/25',
				accentOutline: 'border border-accent text-accent font-semibold hover:bg-accent/10',
				// `.btn-green` del mock v11: fondo verde al 20%, bordo al 40%. È la
				// conferma affermativa accanto a un rifiuto in `outline` — la coppia
				// «✕ No / ✓ Sì» delle raccomandazioni.
				successSoft:
					'border border-accent-green/40 bg-accent-green/20 text-accent-green font-semibold hover:bg-accent-green/30',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-border bg-card text-text-muted hover:border-accent hover:text-accent',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent/10 hover:text-accent',
				ghostDanger: 'text-text-muted hover:bg-danger/10 hover:text-danger',
				link: 'text-accent underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-8 px-3 py-1.5',
				sm: 'h-7 px-2.5 py-1 text-[12px]',
				lg: 'h-10 px-5 text-xs font-bold',
				icon: 'h-8 w-8',
			},
			fullWidth: {
				true: 'w-full',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** Stile visivo del bottone (default, accent, destructive, outline, ghost, link, ecc.). */
	variant?: VariantProps<typeof buttonVariants>['variant'];
	/** Dimensione del bottone. */
	size?: VariantProps<typeof buttonVariants>['size'];
	/** Se true, il bottone occupa tutta la larghezza disponibile. */
	fullWidth?: VariantProps<typeof buttonVariants>['fullWidth'];
	/** Se true, non renderizza un `<button>` ma inoltra stile/props al suo unico figlio (Radix Slot) — utile per usare Button come wrapper di un `<a>` o altro elemento. */
	asChild?: boolean;
}

/** Bottone d'azione standard dell'app: cliccabile, con varianti di stile/dimensione e stato disabled/loading gestito dal chiamante. */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, fullWidth, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
